/**
 * PermissionGate — Inline permission/role check for UI elements
 *
 * Usage:
 *   <PermissionGate role="admin">
 *     <AdminOnlyButton />
 *   </PermissionGate>
 *
 *   <PermissionGate permission="manage_events" fallback={<UpgradePrompt />}>
 *     <EventManager />
 *   </PermissionGate>
 */

import { useAuthStore } from '../../state_management_and_stores/auth.store';

interface PermissionGateProps {
  children: React.ReactNode;
  role?: string;
  roles?: string[];
  permission?: string;
  permissions?: string[];
  anyPermission?: string[];
  fallback?: React.ReactNode;
}

export function PermissionGate({
  children,
  role: requiredRole,
  roles: requiredRoles,
  permission,
  permissions,
  anyPermission,
  fallback = null,
}: PermissionGateProps) {
  const {
    role,
    hasRole,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    isAuthenticated,
    isAdmin,
  } = useAuthStore();

  if (!isAuthenticated) {
    return <>{fallback}</>;
  }

  // Admin bypass — mirror the backend's `requirePermission` middleware,
  // which short-circuits to allow when `request.user?.isAdmin` is true.
  // Without this, an admin who hasn't been explicitly granted a brand-new
  // permission row (e.g. before a fresh re-login, or before a seed
  // migration finishes propagating) loses access to permission-gated UI
  // that they should always see — exactly what was happening to the
  // super-admin client on the Print judge sheet button. Role checks are
  // intentionally NOT bypassed here: if a caller asked for "delegate
  // only", an admin shouldn't satisfy that.
  const isAdminUser = isAdmin();
  const hasPermissionRequirement = !!permission || !!permissions || !!anyPermission;
  if (isAdminUser && hasPermissionRequirement && !requiredRole && !requiredRoles) {
    return <>{children}</>;
  }

  // Role check (single)
  if (requiredRole) {
    const match = hasRole(requiredRole) || role?.role_type === requiredRole;
    if (!match) return <>{fallback}</>;
  }

  // Role check (any of)
  if (requiredRoles && requiredRoles.length > 0) {
    const match = requiredRoles.some(
      (r) => hasRole(r) || role?.role_type === r
    );
    if (!match) return <>{fallback}</>;
  }

  // Single permission
  if (permission && !hasPermission(permission)) {
    return <>{fallback}</>;
  }

  // All permissions required
  if (permissions && !hasAllPermissions(permissions)) {
    return <>{fallback}</>;
  }

  // Any permission
  if (anyPermission && !hasAnyPermission(anyPermission)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
